/**
 * Masks bearer values in URLs before Sentry sends an event, a transaction, a
 * span, a breadcrumb or a log.
 *
 * Some links are their own access: a Teen Insight 360 family report
 * (`/family-report/<token>`), a parent report (`/shared/report/<token>`), a
 * workshop certificate (`/certificate/<slug>`) and its check
 * (`/certificate/verify/<code>`), and a Teen Insight parent form
 * (`?ti=<invite code>`), and a direct-to-consumer test link
 * (`/take/<token>`), report link (`/my-report/<token>`) or invoice link (`/my-invoice/<token>`). The API calls behind them carry the same values
 * (`/teen-insight/shared/<token>`, `/teen-insight/invite/<code>`,
 * `/evaluation-report/shared/<token>`, `/certificates/public/<slug>[/claim]`,
 * `/certificates/public/verify/<code>`). Browser tracing and Session Replay
 * record page and request URLs, so without this anyone who can read Sentry
 * could open a family's report or a parent's form.
 *
 * Only those values are replaced, with `[redacted]`; the rest of the URL and
 * of the event stays as it was, and an event with none of them comes back
 * unchanged. Nested values are copied, never edited in place, so an object
 * the app logged is not changed. The same paths URL-encoded inside another
 * URL (a WhatsApp share link) are masked too.
 */

export const SENTRY_URL_MASK = '[redacted]';

// A path separator, plain or URL-encoded, and the value after it, which ends
// at the next separator, query, fragment, '&', '%' (an encoded separator),
// quote or space. Tokens, slugs and codes are URL-safe, so none holds a '%'.
const SEP = '(?:/|%2F)';
const VALUE = `[^/?#&%\\s"'<>]+`;
// A direct-to-consumer take link is `/take/<token>`. The two API paths under
// `/api/d2c/take/` (`resolve`, `request-new-link`) are actions, not tokens, so they
// stay readable in Sentry; the tokens themselves travel in POST bodies.
const D2C_TOKEN = `(?!(?:resolve|request-new-link)(?:[/?#&%\\s"'<>]|$))${VALUE}`;
const QUERY_START = '(?:[?&]|%3F|%26)';
const EQUALS = '(?:=|%3D)';

const SOURCES = [
    `(${SEP}family-report${SEP})${VALUE}`,
    `(${SEP}certificate${SEP}(?:verify${SEP})?)${VALUE}`,
    `(${SEP}shared${SEP}report${SEP})${VALUE}`,
    `(${SEP}take${SEP})${D2C_TOKEN}`,
    `(${SEP}my-report${SEP})${VALUE}`,
    `(${SEP}my-invoice${SEP})${VALUE}`,
    `(${SEP}teen-insight${SEP}(?:shared|invite)${SEP})${VALUE}`,
    `(${SEP}evaluation-report${SEP}shared${SEP})${VALUE}`,
    `(${SEP}certificates${SEP}public${SEP}(?:verify${SEP})?)${VALUE}`,
    `(${QUERY_START}ti${EQUALS})${VALUE}`
];

const PATTERNS: RegExp[] = SOURCES.map((source) => new RegExp(source, 'gi'));

/**
 * The same links as plain tests, one per kind (no `g` flag, so `test` keeps no
 * state between calls). Session Replay takes them as `networkDetailDenyUrls`.
 */
export const SENTRY_BEARER_URL_PATTERNS: RegExp[] = SOURCES.map((source) => new RegExp(source, 'i'));

// A cheap check first: most strings in an event hold none of these.
const MAYBE = /family-report|certificate|teen-insight|shared|take|my-report|my-invoice|ti(?:=|%3D)/i;

/** One string with every bearer value masked; anything else is returned as is. */
export function maskSentryUrl<T>(text: T): T {
    if (typeof text !== 'string' || !text || !MAYBE.test(text)) return text;
    let out: string = text;
    for (const pattern of PATTERNS) out = out.replace(pattern, `$1${SENTRY_URL_MASK}`);
    return out as unknown as T;
}

/** Whether a URL, or any text, holds one of those links. */
export function isSentryBearerUrl(text: unknown): boolean {
    return typeof text === 'string' && MAYBE.test(text) && SENTRY_BEARER_URL_PATTERNS.some((pattern) => pattern.test(text));
}

const MAX_DEPTH = 4;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (!value || typeof value !== 'object') return false;
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
};

/**
 * Masks strings inside arrays and plain objects, a few levels deep. Returns
 * the same reference when nothing changed, and a copy otherwise. Errors,
 * DOM nodes and other class instances are left alone.
 */
function maskDeep<T>(value: T, depth = 0, maxDepth = MAX_DEPTH): T {
    if (typeof value === 'string') return maskSentryUrl(value);
    if (depth >= maxDepth) return value;
    if (Array.isArray(value)) {
        let changed = false;
        const next = value.map((item) => {
            const masked = maskDeep(item, depth + 1, maxDepth);
            if (masked !== item) changed = true;
            return masked;
        });
        return (changed ? next : value) as T;
    }
    if (isPlainObject(value)) {
        let next: Record<string, unknown> | null = null;
        for (const [key, item] of Object.entries(value)) {
            const masked = maskDeep(item, depth + 1, maxDepth);
            if (masked !== item) {
                next = next ?? { ...value };
                next[key] = masked;
            }
        }
        return (next ?? value) as T;
    }
    return value;
}

type Loose = Record<string, any>;

/** `request.query_string` may be a string, an object or a list of pairs; the `ti` value is masked in each. */
function maskQueryString(query: unknown): unknown {
    if (typeof query === 'string') {
        const masked = maskSentryUrl(`?${query}`);
        return masked === `?${query}` ? query : masked.slice(1);
    }
    if (Array.isArray(query)) {
        let changed = false;
        const next = query.map((pair) => {
            if (Array.isArray(pair) && pair[0] === 'ti' && pair[1] !== SENTRY_URL_MASK) {
                changed = true;
                return [pair[0], SENTRY_URL_MASK];
            }
            return pair;
        });
        return changed ? next : query;
    }
    if (isPlainObject(query) && typeof query.ti === 'string' && query.ti !== SENTRY_URL_MASK) {
        return { ...query, ti: SENTRY_URL_MASK };
    }
    return query;
}

/** A span (a transaction's child span, or a standalone one): its description and data. */
export function scrubSentrySpanUrls<T>(span: T): T {
    if (!span || typeof span !== 'object') return span;
    const s = span as Loose;
    if (typeof s.description === 'string') s.description = maskSentryUrl(s.description);
    if (s.data) s.data = maskDeep(s.data);
    return span;
}

/** A breadcrumb: its message and data. A breadcrumb with nothing to mask comes back as the same object. */
export function scrubSentryBreadcrumbUrls<T>(breadcrumb: T): T {
    if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
    const b = breadcrumb as Loose;
    const message = maskSentryUrl(b.message);
    const data = maskDeep(b.data);
    if (message === b.message && data === b.data) return breadcrumb;
    return { ...b, message, data } as T;
}

/**
 * An error or transaction event: the page URL and referrer, the transaction
 * name, the message, exception messages, breadcrumbs, spans, the root span's
 * data, tags and extra.
 */
export function scrubSentryEventUrls<T>(event: T): T {
    if (!event || typeof event !== 'object') return event;
    const e = event as Loose;

    if (typeof e.message === 'string') e.message = maskSentryUrl(e.message);
    if (typeof e.transaction === 'string') e.transaction = maskSentryUrl(e.transaction);
    if (e.logentry) e.logentry = maskDeep(e.logentry);

    if (isPlainObject(e.request)) {
        const { query_string: query, ...rest } = e.request;
        const maskedRest = maskDeep(rest);
        const maskedQuery = maskQueryString(query);
        if (maskedRest !== rest || maskedQuery !== query) {
            e.request = 'query_string' in e.request ? { ...maskedRest, query_string: maskedQuery } : maskedRest;
        }
    }

    if (Array.isArray(e.exception?.values)) {
        e.exception.values = e.exception.values.map((value: Loose) => {
            if (!value || typeof value.value !== 'string') return value;
            const masked = maskSentryUrl(value.value);
            return masked === value.value ? value : { ...value, value: masked };
        });
    }

    if (Array.isArray(e.breadcrumbs)) e.breadcrumbs = e.breadcrumbs.map((crumb: unknown) => scrubSentryBreadcrumbUrls(crumb));
    if (Array.isArray(e.spans)) e.spans = e.spans.map((span: unknown) => scrubSentrySpanUrls(span));
    if (isPlainObject(e.contexts?.trace) && e.contexts.trace.data) {
        const data = maskDeep(e.contexts.trace.data);
        if (data !== e.contexts.trace.data) e.contexts = { ...e.contexts, trace: { ...e.contexts.trace, data } };
    }
    if (e.tags) e.tags = maskDeep(e.tags);
    if (e.extra) e.extra = maskDeep(e.extra);

    return event;
}

/** A Sentry log: its message and string attributes. */
export function scrubSentryLogUrls<T>(log: T): T {
    if (!log || typeof log !== 'object') return log;
    const l = log as Loose;
    if (typeof l.message === 'string') l.message = maskSentryUrl(l.message);
    if (l.attributes) l.attributes = maskDeep(l.attributes);
    return log;
}

/*
 * Session Replay sends what it records outside the hooks above: a
 * `replay_event` per segment, and the recording itself.
 */

/**
 * A `replay_event`: the page URLs visited during the segment, plus the fields
 * an error event has (page URL and referrer, transaction, tags, extra).
 */
export function scrubSentryReplayEventUrls<T>(event: T): T {
    if (!event || typeof event !== 'object') return event;
    const e = event as Loose;
    if (Array.isArray(e.urls)) e.urls = maskDeep(e.urls);
    return scrubSentryEventUrls(event);
}

// A recording event nests deeper than an event field does:
// data > payload > data > request > headers.
const REPLAY_MAX_DEPTH = MAX_DEPTH + 2;

/**
 * A recording event the replay SDK adds itself (`beforeAddRecordingEvent`): a
 * breadcrumb, a navigation, a request or a page load timing. Returns the same
 * event when nothing changed, and a copy otherwise.
 *
 * rrweb's own events never reach that hook: the page snapshot, which holds
 * the page URL as `href`, and DOM changes. See `isSentryReplayOnBearerPage`
 * and `SENTRY_REPLAY_BLOCK_SELECTORS`.
 */
export function scrubSentryReplayRecordingEvent<T>(event: T): T {
    if (!event || typeof event !== 'object') return event;
    const e = event as Loose;
    const data = maskDeep(e.data, 0, REPLAY_MAX_DEPTH);
    return data === e.data ? event : ({ ...e, data } as T);
}

/**
 * Whether the replay should stop: the visitor is on one of those pages, or the
 * recording event is a navigation to one. A page snapshot taken there would
 * hold the raw link, and no replay hook can rewrite a snapshot.
 */
export function isSentryReplayOnBearerPage(event: unknown, pageUrl: string): boolean {
    if (isSentryBearerUrl(pageUrl)) return true;
    const payload = (event as Loose)?.data?.payload;
    return typeof payload?.op === 'string' && payload.op.startsWith('navigation.') && isSentryBearerUrl(payload.description);
}

// The same links as they sit in an href or src attribute: as they are, and
// URL-encoded inside another link (a WhatsApp share button).
const LINK_MARKERS = [
    '/family-report/',
    '/shared/report/',
    '/take/',
    '/my-report/',
    '/my-invoice/',
    '/certificate/',
    '/teen-insight/shared/',
    '/teen-insight/invite/',
    '/evaluation-report/shared/',
    '/certificates/public/',
    '?ti=',
    '&ti='
].flatMap((marker) => [marker, encodeURIComponent(marker)]);

/**
 * Session Replay `block` selectors. Replay masks text and inputs, but records
 * href and src as they are; an element linking to one of those pages is
 * recorded as an empty box of the same size instead.
 */
export const SENTRY_REPLAY_BLOCK_SELECTORS: string[] = LINK_MARKERS.flatMap((marker) => [
    `[href*="${marker}" i]`,
    `[src*="${marker}" i]`
]);
